import React from "react";
import { Routes, Route, Navigate, BrowserRouter } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AppShell from "./components/AppShell";
import Dashboard from "./pages/Dashboard";
import Loads from "./pages/Loads";
import LoadDetail from "./pages/LoadDetail";
import NewLoad from "./pages/NewLoad";
import Bids from "./pages/Bids";
import Notifications from "./pages/Notifications";
import Billing from "./pages/Billing";
import Documents from "./pages/Documents";
import AdminUsers from "./pages/AdminUsers";
import AdminFeeRules from "./pages/AdminFeeRules";
import AdminLogs from "./pages/AdminLogs";
import AdminBrokerForms from "./pages/AdminBrokerForms";
import DriverMatches from "./pages/DriverMatches";
import Tracking from "./pages/Tracking";
import Quotes from "./pages/Quotes";
import IntakeForm from "./pages/IntakeForm";
import AdminIntake from "./pages/AdminIntake";
import RoleOnboarding from "./pages/RoleOnboarding";
import SuperAdminPortal from "./pages/SuperAdminPortal";

function needsRoleSelection(user) {
  return user?.role === "pending" || user?.onboarding_required === true;
}

function Protected({ children, allowPending = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (needsRoleSelection(user) && !allowPending) {
    return <Navigate to="/app/onboarding/role" replace />;
  }
  if (!needsRoleSelection(user) && allowPending) {
    return <Navigate to="/app" replace />;
  }
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/superadmin" element={<SuperAdminPortal />} />
      <Route path="/intake" element={<IntakeForm />} />
      <Route path="/app/onboarding/role" element={<Protected allowPending><RoleOnboarding /></Protected>} />
      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="loads" element={<Loads />} />
        <Route path="board" element={<Loads board />} />
        <Route path="loads/new" element={<NewLoad />} />
        <Route path="loads/:id" element={<LoadDetail />} />
        <Route path="bids" element={<Bids />} />
        <Route path="matches" element={<DriverMatches />} />
        <Route path="documents" element={<Documents />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="billing" element={<Billing />} />
        <Route path="tracking" element={<Tracking />} />
        <Route path="quotes" element={<Quotes />} />
        <Route path="admin/intake" element={<AdminIntake />} />
        <Route path="admin/users" element={<AdminUsers />} />
        <Route path="admin/fee-rules" element={<AdminFeeRules />} />
        <Route path="admin/broker-forms" element={<AdminBrokerForms />} />
        <Route path="admin/logs" element={<AdminLogs />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}
